import { deleteCookie } from './cookies'

/**
 * Checks if the user has basic admin roles (admin or general_admin)
 * @param roles - Array of user roles (can be undefined or null)
 * @returns true if user has admin or general_admin role
 */
export const isAdmin = (roles?: string[] | null): boolean => {
  if (!roles || roles.length === 0) return false
  return roles.includes('admin') || roles.includes('general_admin')
}

/**
 * Checks if the user has elevated admin roles
 * Includes admin, super_admin, sales_manager, or general_admin
 * @param roles - Array of user roles (can be undefined or null)
 * @returns true if user has any elevated admin role
 */
export const isAdminPlus = (roles?: string[] | null): boolean => {
  if (!roles || roles.length === 0) return false
  return (
    roles.includes('admin') ||
    roles.includes('super_admin') ||
    roles.includes('sales_manager') ||
    roles.includes('general_admin')
  )
}

/**
 * Checks if the user has the 'admin' role specifically
 * @param roles - Array of user roles (can be undefined or null)
 * @returns true if user has the admin role
 */
export const hasAdminRole = (roles?: string[] | null): boolean => {
  if (!roles || roles.length === 0) return false
  return roles.includes('admin')
}

/**
 * Checks if the user has the 'pro' role
 * @param roles - Array of user roles (can be undefined or null)
 * @returns true if user has the pro role
 */
export const isPro = (roles?: string[] | null): boolean => {
  if (!roles || roles.length === 0) return false
  return roles.includes('pro')
}

/**
 * Checks if the user has pro role
 * @param roles - Array of user roles
 * @returns Object with isPro boolean and userType ('pro' | null)
 */
export const checkAdminProRoles = (roles: string[]): { isPro: boolean; userType: 'pro' | null } => {
  const userIsPro = isPro(roles)

  if (userIsPro) {
    return { isPro: true, userType: 'pro' }
  }

  return { isPro: false, userType: null }
}

/**
 * Drops the pro cookie by deleting it
 * This function attempts to delete the cookie for multiple domain variations
 */
export const dropAdminProCookie = (): void => {
  if (typeof window === 'undefined') return

  const cookieName = 'frontend_app_booking_flow'

  // Delete cookie for current domain and various domain variations
  deleteCookie(cookieName)
  deleteCookie(cookieName, window.location.hostname)

  // Try common domain variations
  if (window.location.hostname.includes('.')) {
    const domainParts = window.location.hostname.split('.')
    if (domainParts.length >= 2) {
      const rootDomain = `.${domainParts.slice(-2).join('.')}`
      deleteCookie(cookieName, rootDomain)
    }
  }
}

/**
 * Handles pro user redirect by dropping the cookie and opening the modal
 * @param roles - Array of user roles
 * @param openModal - Function to open the redirect modal
 * @returns true if user is pro and redirect was triggered, false otherwise
 */
export const handleAdminProRedirect = (
  roles: string[],
  openModal: (modalId: string) => void,
): boolean => {
  const { isPro, userType } = checkAdminProRoles(roles)

  if (isPro && userType) {
    // Drop the cookie
    dropAdminProCookie()

    // Open the redirect modal
    openModal('pro-redirect-modal')

    return true
  }

  return false
}
